// Fechamento com as mesmas métricas e quatro janelas no desktop e no celular.
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronDown, HelpCircle } from 'lucide-react';
import ExplicaKpi, { TipoKpi } from './ExplicaKpi';
import { Kpis, KpisJanelas, SignoffLinha } from '../types';
import { fmtMoney, fmtPct, fmtPp } from '../utils/format';

type CampoKpi = 'ns_qtd' | 'pct_no_prazo' | 'pct_corte';

interface KpiDef {
  titulo: string;
  abrev: string;
  tipo: TipoKpi;
  campo: CampoKpi;
  fmt: (n: number | null | undefined) => string;
  fmtDelta: (n: number | null | undefined) => string;
  direcao: 'up' | 'down';
  extra?: { campo: keyof Kpis; fmt: (n: number | null | undefined) => string; sufixo?: string };
}

const KPIS: KpiDef[] = [
  {
    titulo: 'NS Quantidade', abrev: 'NS Qtd.', tipo: 'ns', campo: 'ns_qtd', fmt: fmtPct, fmtDelta: fmtPp, direcao: 'up',
    extra: { campo: 'pct_apurado', fmt: fmtPct, sufixo: ' faturado' },
  },
  { titulo: '% no Prazo', abrev: 'No prazo', tipo: 'prazo', campo: 'pct_no_prazo', fmt: fmtPct, fmtDelta: fmtPp, direcao: 'up' },
  {
    titulo: 'Corte', abrev: 'Corte', tipo: 'corte', campo: 'pct_corte', fmt: fmtPct, fmtDelta: fmtPp, direcao: 'down',
    extra: { campo: 'corte_valor', fmt: fmtMoney },
  },
];

function Delta({ mtd, m1, direcao, fmtDelta }: { mtd: number | null; m1: number | null; direcao: 'up' | 'down'; fmtDelta: KpiDef['fmtDelta'] }) {
  if (mtd === null || m1 === null) return <span className="muted" title="Comparação indisponível">—</span>;
  const d = mtd - m1;
  if (Math.abs(d) < 1e-9) return <span className="muted" title="Sem variação">=</span>;
  const favoravel = direcao === 'up' ? d > 0 : d < 0;
  return (
    <span className={favoravel ? 'delta-good' : 'delta-bad'} title={`${favoravel ? 'Melhora' : 'Piora'} em relação ao mês anterior`}>
      <span aria-hidden="true">{d > 0 ? '▲' : '▼'}</span> {fmtDelta(Math.abs(d))}
      <span className="sr-only"> · {favoravel ? 'melhora' : 'piora'}</span>
    </span>
  );
}

interface Props {
  total: SignoffLinha | null;
  linhas: SignoffLinha[];
  refLabel: string;
  m1Label?: string;
}

interface Explicando {
  kpi: KpiDef;
  label: string;
  kpis: KpisJanelas;
}

export default function SignoffTable({ total, linhas, refLabel, m1Label }: Props) {
  const [explicando, setExplicando] = useState<Explicando | null>(null);
  const abrir = (kpi: KpiDef, linha: SignoffLinha) => setExplicando({ kpi, label: linha.label, kpis: linha.kpis });
  const mobileRows = total ? [total, ...linhas] : linhas;
  const janelas: { id: 'hoje' | 'mtd' | 'm1' | 'ref'; label: string }[] = [
    { id: 'hoje', label: 'Hoje' },
    { id: 'mtd', label: 'MTD' },
    { id: 'm1', label: m1Label || 'M-1' },
    { id: 'ref', label: refLabel },
  ];

  const CelulasKpi = ({ linha, kpi }: { linha: SignoffLinha; kpi: KpiDef }) => {
    const kpis = linha.kpis;
    const v = (w: 'hoje' | 'mtd' | 'm1' | 'ref') => kpis[w][kpi.campo];
    return (
      <>
        <td className="num sep">{kpi.fmt(v('hoje'))}</td>
        <td className="num mtd">
          <button type="button" className="kpi-click" aria-label={`Ver cálculo de ${kpi.titulo} de ${linha.label}`} onClick={() => abrir(kpi, linha)}>{kpi.fmt(v('mtd'))}</button>
          {kpi.extra && <div className="muted text-[10.5px]">{kpi.extra.fmt(kpis.mtd[kpi.extra.campo] as number)}{kpi.extra.sufixo || ''}</div>}
        </td>
        <td className="num">{kpi.fmt(v('m1'))}</td>
        <td className="num">{kpi.fmt(v('ref'))}</td>
        <td className="num"><Delta mtd={v('mtd')} m1={v('m1')} direcao={kpi.direcao} fmtDelta={kpi.fmtDelta} /></td>
      </>
    );
  };

  return (
    <>
      <div className="table-wrap desktop-only" tabIndex={0} role="region" aria-label="Tabela de fechamento. Role horizontalmente para ver todos os períodos.">
        <table className="signoff">
          <caption className="sr-only">Indicadores por recorte nas janelas Hoje, MTD, mês anterior e referência.</caption>
          <thead>
            <tr>
              <th rowSpan={2} scope="col" className="align-bottom">Recorte</th>
              {KPIS.map((k) => (
                <th key={k.titulo} colSpan={5} scope="colgroup" className="group sep">
                  <span className="inline-flex items-center gap-1.5">{k.titulo}{total && <button type="button" className="dica-btn" aria-label={`Como é calculado ${k.titulo}?`} onClick={() => abrir(k, total)}><HelpCircle size={13} /></button>}</span>
                </th>
              ))}
            </tr>
            <tr>
              {KPIS.map((k) => (
                <Fragment key={k.titulo}>
                  <th scope="col" className="num sub sep">Hoje</th>
                  <th scope="col" className="num sub mtd">MTD</th>
                  <th scope="col" className="num sub" title="Comparação justa: mês anterior até o mesmo dia">{m1Label || 'M-1'}</th>
                  <th scope="col" className="num sub">{refLabel}</th>
                  <th scope="col" className="num sub">Δ M-1</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.key}>
                <td>{l.link ? <Link to={`/service-level${l.link}`}><strong>{l.label}</strong></Link> : <strong>{l.label}</strong>}{l.sublabel && <div className="muted text-[11px]">{l.sublabel}</div>}</td>
                {KPIS.map((k) => <CelulasKpi key={k.titulo} linha={l} kpi={k} />)}
              </tr>
            ))}
            {total && <tr className="total-row"><td><strong>{total.label}</strong></td>{KPIS.map((k) => <CelulasKpi key={k.titulo} linha={total} kpi={k} />)}</tr>}
          </tbody>
        </table>
      </div>

      <div className="mobile-only signoff-mobile-list" role="region" aria-label="Fechamento em cartões">
        {mobileRows.map((l) => {
          const isTotal = total?.key === l.key;
          const semMes = !(Number(l.kpis.mtd.pedidos_recebidos) > 0 || Number(l.kpis.mtd.qtd_comprada) > 0);
          const semApuracao = !semMes && !(Number(l.kpis.mtd.qtd_comprada) > 0);
          return (
            <article key={l.key} className={`signoff-mobile-card ${isTotal ? 'is-total' : ''}`} aria-label={l.label}>
              <div className="signoff-mobile-header">
                <div><span className="section-eyebrow">{isTotal ? 'CONSOLIDADO · MTD' : 'MÊS CORRENTE · MTD'}</span><h3>{l.label}</h3>{l.sublabel && <p className="muted">{l.sublabel}</p>}</div>
                {l.link && <Link to={`/service-level${l.link}`} className="signoff-mobile-detail-link" aria-label={`Ver detalhes de ${l.label}`}><ArrowUpRight size={18} aria-hidden="true" /><span className="sr-only">Ver detalhes</span></Link>}
              </div>
              {semMes ? <p className="signoff-mobile-note">Sem movimento no mês. Consulte os outros períodos abaixo.</p> : semApuracao ? <p className="signoff-mobile-note">{l.kpis.mtd.pedidos_recebidos} pedido(s) no mês, ainda sem volume faturado.</p> : null}
              <div className="signoff-mobile-summary">
                {KPIS.map((k) => (
                  <div key={k.campo}>
                    <span className="muted">{k.abrev}</span>
                    <button type="button" className="kpi-click" aria-label={`Ver cálculo de ${k.titulo} de ${l.label}`} onClick={() => abrir(k, l)}>{k.fmt(l.kpis.mtd[k.campo])}</button>
                    <div className="signoff-mobile-delta"><Delta mtd={l.kpis.mtd[k.campo]} m1={l.kpis.m1[k.campo]} direcao={k.direcao} fmtDelta={k.fmtDelta} /></div>
                    {k.extra && <span className="signoff-mobile-extra muted">{k.extra.fmt(l.kpis.mtd[k.extra.campo] as number)}{k.extra.sufixo || ''}</span>}
                  </div>
                ))}
              </div>
              <details className="signoff-mobile-details" aria-label={`Comparação de períodos de ${l.label}`} open={isTotal && linhas.length === 0 ? true : undefined}>
                <summary>Comparar as 4 janelas <ChevronDown size={16} aria-hidden="true" /></summary>
                <div className="signoff-mobile-kpis">
                  {KPIS.map((k) => (
                    <div className="signoff-mobile-kpi" key={k.campo}>
                      <div className="signoff-mobile-kpi-title"><strong>{k.titulo}</strong><button type="button" className="dica-btn" aria-label={`Entender ${k.titulo} de ${l.label}`} onClick={() => abrir(k, l)}><HelpCircle size={14} /></button></div>
                      <div className="signoff-mobile-windows" role="list" aria-label={`Períodos de ${k.titulo}`}>
                        {janelas.map((j) => <div key={j.id} role="listitem" aria-label={j.label} className={j.id === 'mtd' ? 'is-current' : ''}><span className="muted">{j.label}</span><strong>{k.fmt(l.kpis[j.id][k.campo])}</strong></div>)}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            </article>
          );
        })}
      </div>

      {explicando && <ExplicaKpi tipo={explicando.kpi.tipo} label={explicando.label} kpis={explicando.kpis} m1Label={m1Label} onClose={() => setExplicando(null)} />}
    </>
  );
}
